import React, { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

const PROFILE_MENU_CLOSE_DELAY_MS = 250

const UserEmailBadge = ({ email, showProfileMenu = false }) => {
  const { t } = useTranslation('dashboard')
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const menuRootRef = useRef(null)
  const triggerRef = useRef(null)
  const menuCloseTimeoutRef = useRef(null)

  useEffect(() => () => {
    if (menuCloseTimeoutRef.current) clearTimeout(menuCloseTimeoutRef.current)
  }, [])

  useEffect(() => {
    if (!isMenuOpen) return undefined

    const closeOnOutsidePointer = (event) => {
      if (!menuRootRef.current?.contains(event.target)) setIsMenuOpen(false)
    }

    document.addEventListener('pointerdown', closeOnOutsidePointer)
    return () => document.removeEventListener('pointerdown', closeOnOutsidePointer)
  }, [isMenuOpen])

  const cancelPendingMenuClose = () => {
    if (menuCloseTimeoutRef.current) {
      clearTimeout(menuCloseTimeoutRef.current)
      menuCloseTimeoutRef.current = null
    }
  }

  const handleMenuMouseEnter = () => {
    cancelPendingMenuClose()
    const supportsHover = window.matchMedia?.('(hover: hover) and (pointer: fine)').matches
    if (supportsHover) setIsMenuOpen(true)
  }

  const handleMenuMouseLeave = () => {
    const supportsHover = window.matchMedia?.('(hover: hover) and (pointer: fine)').matches
    if (!supportsHover) return

    cancelPendingMenuClose()
    menuCloseTimeoutRef.current = setTimeout(() => {
      menuCloseTimeoutRef.current = null
      if (!menuRootRef.current?.contains(document.activeElement)) setIsMenuOpen(false)
    }, PROFILE_MENU_CLOSE_DELAY_MS)
  }

  if (!email) return null

  if (showProfileMenu) {
    const closeMenuIfFocusLeaves = (event) => {
      if (!menuRootRef.current?.contains(event.relatedTarget)) setIsMenuOpen(false)
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        setIsMenuOpen(false)
        triggerRef.current?.focus()
      }
    }

    return (
      <div
        ref={menuRootRef}
        className="relative flex items-center text-sm text-gray-700"
        onMouseEnter={handleMenuMouseEnter}
        onMouseLeave={handleMenuMouseLeave}
        onBlurCapture={closeMenuIfFocusLeaves}
        onKeyDown={handleKeyDown}
      >
        <button
          ref={triggerRef}
          type="button"
          aria-haspopup="true"
          aria-expanded={isMenuOpen}
          aria-controls="customer-profile-menu"
          onClick={(event) => {
            const supportsHover = window.matchMedia?.('(hover: hover) and (pointer: fine)').matches
            if (supportsHover && event.detail > 0) {
              setIsMenuOpen(true)
            } else {
              setIsMenuOpen((open) => !open)
            }
          }}
          className="flex items-center space-x-2 rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FF1B54] focus-visible:ring-offset-2"
        >
          <span aria-hidden="true">
            <svg viewBox="0 0 24 24" className="w-6 h-6">
              <circle cx="12" cy="9" r="3.2" fill="currentColor" opacity="0.85" />
              <path
                d="M6.5 18.2c.9-2.3 2.7-3.7 5.5-3.7s4.6 1.4 5.5 3.7"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.4"
                strokeLinecap="round"
              />
            </svg>
          </span>
          <span>{email}</span>
        </button>

        {isMenuOpen && (
          <div
            id="customer-profile-menu"
            role="group"
            aria-label={t('customer.accountMenuLabel')}
            className="absolute right-0 top-full z-50 mt-2 w-max min-w-[224px] rounded-xl border border-gray-200 bg-white p-1.5 shadow-lg shadow-gray-900/10"
          >
            <Link
              to="/customer/profile"
              onClick={() => setIsMenuOpen(false)}
              className="block rounded-lg px-3 py-2.5 text-sm font-medium text-gray-700 transition-colors hover:bg-[#FFF1F4] hover:text-[#E1174C] focus:bg-[#FFF1F4] focus:text-[#E1174C] focus:outline-none"
            >
              {t('customer.yourBusinessProfile')}
            </Link>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="flex items-center space-x-2 text-sm text-gray-700">
      <span aria-hidden="true">
        <svg viewBox="0 0 24 24" className="w-6 h-6">
          <circle cx="12" cy="9" r="3.2" fill="currentColor" opacity="0.85" />
          <path
            d="M6.5 18.2c.9-2.3 2.7-3.7 5.5-3.7s4.6 1.4 5.5 3.7"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.4"
            strokeLinecap="round"
          />
        </svg>
      </span>
      <span>{email}</span>
    </div>
  )
}

export default UserEmailBadge
